import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import Layout from '@components/Layout';
import Swal from 'sweetalert2';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import Modal from '@/components/Modal';

function MarketplaceAdmin() {
  const [categories, setCategories] = useState([]);
  const [newParentCategory, setNewParentCategory] = useState('');
  const [newChildCategory, setNewChildCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  // keyed by parentId -> the text typed for that parent's subcategory input
  const [subNameByParent, setSubNameByParent] = useState({});

  // Fetch categories and adverts
  const fetchCategoriesAndAdverts = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch categories
      const categorySnapshot = await getDocs(
        collection(db, 'marketplaceCategories')
      );
      let categoryList = categorySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        activeCount: 0,
        futureCount: 0,
      }));

      // Fetch adverts
      const advertsSnapshot = await getDocs(collection(db, 'adverts'));
      const adverts = advertsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log('Fetched adverts from Firestore:', adverts);

      const today = dayjs().startOf('day');

      // Assign advert counts to sub-categories
      const updatedCategories = categoryList.map((category) => {
        const categoryAdverts = adverts.filter(
          (ad) => ad.subCategoryId === category.id
        );

        console.log(
          `Checking sub-category ${category.id}: Found ${categoryAdverts.length} adverts`
        );

        const activeCount = categoryAdverts.filter((ad) => {
          const startDate = ad.startDate?.seconds
            ? dayjs.unix(ad.startDate.seconds)
            : null;
          const endDate = ad.endDate?.seconds
            ? dayjs.unix(ad.endDate.seconds)
            : null;

          const isActive =
            (startDate && today.isSame(startDate)) ||
            (today.isAfter(startDate) && (!endDate || today.isBefore(endDate)));

          console.log(
            `Advert ${
              ad.id
            }: Start ${startDate?.format()}, End ${endDate?.format()}, Active: ${isActive}`
          );
          return isActive;
        }).length;

        const futureCount = categoryAdverts.filter((ad) => {
          const startDate = ad.startDate?.seconds
            ? dayjs.unix(ad.startDate.seconds)
            : null;
          return startDate && today.isBefore(startDate);
        }).length;

        console.log(
          `Sub-category ${category.id}: ${activeCount} Active, ${futureCount} Future`
        );

        return { ...category, activeCount, futureCount };
      });

      setCategories(updatedCategories);
    } catch (error) {
      console.error('Error fetching categories or adverts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategoriesAndAdverts();
  }, [fetchCategoriesAndAdverts]);

  // Create parent category
  const handleCreateParentCategory = async () => {
    if (!newParentCategory.trim()) return;
    try {
      const newCategory = {
        name: newParentCategory.trim(),
        dateCreated: new Date().toISOString(),
      };
      const docRef = await addDoc(
        collection(db, 'marketplaceCategories'),
        newCategory
      );
      setCategories((prev) => [
        ...prev,
        { id: docRef.id, ...newCategory, activeCount: 0, futureCount: 0 },
      ]);
      setNewParentCategory('');
      toast.success('Parent category created successfully!');
    } catch (error) {
      console.error('Error creating parent category:', error);
      toast.error('Failed to create category.');
    }
  };

  // Create child category
  const handleCreateChildCategory = async (parentId) => {
    const raw = (subNameByParent[parentId] || '').trim();

    // Basic validation
    if (!raw) {
      toast.error('Enter a sub category name');
      return;
    }

    // Optional: prevent duplicates under the same parent
    const dup = categories.some(
      (c) =>
        c.parent === parentId &&
        (c.name || '').toLowerCase() === raw.toLowerCase()
    );
    if (dup) {
      toast.error(
        'A sub category with that name already exists in this parent'
      );
      return;
    }

    try {
      const newCategory = {
        name: raw,
        parent: parentId, // <-- IMPORTANT: link to parent
        dateCreated: new Date().toISOString(),
      };

      const docRef = await addDoc(
        collection(db, 'marketplaceCategories'),
        newCategory
      );

      // Update UI immediately so it appears without a full reload
      setCategories((prev) => [
        ...prev,
        { id: docRef.id, ...newCategory, activeCount: 0, futureCount: 0 },
      ]);

      // Clear just this parent's input box
      setSubNameByParent((prev) => ({ ...prev, [parentId]: '' }));

      toast.success('Sub category created successfully!');
    } catch (error) {
      console.error('Error creating sub category:', error);
      toast.error('Failed to create sub category. Please try again.');
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Deleting this category will also delete all its subcategories.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      // find all categories that are either the parent or descendants
      const toDelete = [categoryId];

      const findChildren = (parentId) => {
        categories
          .filter((c) => c.parent === parentId)
          .forEach((child) => {
            toDelete.push(child.id);
            findChildren(child.id); // recurse for nested subcategories
          });
      };

      findChildren(categoryId);

      // Delete from Firestore
      for (const id of toDelete) {
        await deleteDoc(doc(db, 'marketplaceCategories', id));
      }

      // Update local state
      setCategories((prev) => prev.filter((c) => !toDelete.includes(c.id)));

      toast.success('Category and its subcategories deleted!');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category.');
    }
  };

  // Toggle accordion
  const toggleAccordion = (categoryId) => {
    setActiveCategoryId((prev) => (prev === categoryId ? null : categoryId));
  };

  return (
    <Layout isAdminPage={true}>
      {/* Breadcrumb */}
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Administration</li>
            <li className='breadcrumb-item'>Marketplace</li>
          </ol>
        </div>
        <div className='col-lg-4'></div>
      </div>

      {/* Sidebar */}
      <div className='col-xl-12 col-lg-4'>
        <div className='row'>
          <div className='col-xl-3 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Marketplace Categories</h5>
              </div>
              <div className='card-body'>
                {/* Create Parent Category */}
                <div className='mb-4'>
                  <div className='d-flex align-items-center'>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Create Root Library'
                        value={newParentCategory}
                        onChange={(e) => setNewParentCategory(e.target.value)}
                      />
                      <button
                        className='btn btn-primary btn-xs'
                        type='button'
                        onClick={handleCreateParentCategory}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                  <div className='col-12 mt-2'>
                    <span className='badge bg-primary ms-2'>
                      Active Adverts
                    </span>
                    <span className='badge bg-warning ms-2'>
                      Future Adverts
                    </span>
                  </div>
                </div>

                {/* Listing of Categories */}
                {loading ? (
                  <p>Loading categories...</p>
                ) : (
                  categories
                    .filter((cat) => !cat.parent)
                    .map((parent) => (
                      <div key={parent.id} className='libraryAccordion mb-2'>
                        <a
                          className='text-body fw-medium py-1 d-flex align-items-center'
                          data-bs-toggle='collapse'
                          href={`#category-collapse-${parent.id}`}
                          role='button'
                          aria-expanded={activeCategoryId === parent.id}
                          aria-controls={`category-collapse-${parent.id}`}
                          onClick={() => toggleAccordion(parent.id)}
                        >
                          <i
                            className={`mdi ${
                              activeCategoryId === parent.id
                                ? 'mdi-folder-open'
                                : 'mdi-folder'
                            } font-size-20 text-warning me-2`}
                          ></i>
                          {parent.name}
                          <i
                            className={`mdi mdi-chevron-${
                              activeCategoryId === parent.id ? 'down' : 'up'
                            } accor-down-icon ms-auto`}
                          />
                          {/* Parent Delete */}
                          <button
                            className='deleteLibrary btn btn-link p-0 ms-2'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCategory(parent.id); // ✅ now wired
                            }}
                          >
                            <i
                              className='fa fa-trash font-12 align-middle me-2'
                              style={{ color: '#cccccc' }}
                            />
                          </button>
                          {/* Parent Count */}
                        </a>

                        {/* Child Categories */}
                        <div
                          id={`category-collapse-${parent.id}`}
                          className={`collapse ${
                            activeCategoryId === parent.id ? 'show' : ''
                          }`}
                        >
                          <ul className='list-unstyled mt-2 ps-4'>
                            {categories
                              .filter((child) => child.parent === parent.id)
                              .map((child) => (
                                <li
                                  key={child.id}
                                  className='d-flex align-items-center justify-content-between'
                                >
                                  <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2' />
                                  {child.name}
                                  {/* Show active count only if greater than 0 */}
                                  {child.activeCount > 0 && (
                                    <span className='badge bg-primary ms-2 badge-sm'>
                                      {child.activeCount}
                                    </span>
                                  )}

                                  {/* Show future count only if greater than 0 */}
                                  {child.futureCount > 0 && (
                                    <span className='badge bg-warning ms-2 badge-sm'>
                                      {child.futureCount}
                                    </span>
                                  )}
                                  {/* Delete Sub Folder */}
                                  <button
                                    className='btn btn-link p-0 ms-auto'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCategory(child.id);
                                    }}
                                  >
                                    <i
                                      className='fa fa-trash font-12 align-middle me-2'
                                      style={{ color: '#cccccc' }}
                                    />
                                  </button>
                                </li>
                              ))}
                          </ul>

                          {/* Sub-library creation input */}
                          <div className='d-flex align-items-center mt-2'>
                            <div className='col-lg-8'>
                              <div className='input-group'>
                                <input
                                  type='text'
                                  className='form-control'
                                  placeholder='Create Sub Category'
                                  value={subNameByParent[parent.id] || ''} // <-- controlled per parent
                                  onChange={(e) =>
                                    setSubNameByParent((prev) => ({
                                      ...prev,
                                      [parent.id]: e.target.value,
                                    }))
                                  }
                                />
                                <button
                                  className='btn btn-warning btn-xs'
                                  type='button'
                                  onClick={() =>
                                    handleCreateChildCategory(parent.id)
                                  }
                                >
                                  {' '}
                                  Create
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MarketplaceAdmin;
